import { FC, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Button, IconButton } from '@shared/components';
import { FormInput } from '@components/FormInput/ui/FormInput.tsx';
import ArrowIcon from '@icons/ArrowIcon/ArrowIcon.tsx';
import {
  verifyOTP,
  resendOTP,
  generateOTP,
  hasPhoneNumber,
} from '@api/auth/apiService.ts';
import {
  GenerateOTPResponse,
  VerifyOTPResponse,
} from '@api/auth/model/types.ts';
import toast from 'react-hot-toast';
import { Toast } from '@components/Toast/ui/Toast.tsx';
import { useNavigate } from 'react-router-dom';
import { SignUpFormDefaultValues } from '@providers/SignUpFormProvider/model/types';
import { useSignUpFormContext } from '@providers/SignUpFormProvider/model/SignUpFormContext';
import { object } from 'yup';
import * as yup from 'yup';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';
import { routes } from '@shared/constants/paths.ts';
import Grid from '@mui/material/Grid';
import {
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  FormControlLabel,
  Radio,
} from '@mui/material';
import Box from '@mui/material/Box';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { useMask } from '@react-input/mask';
import usePageTitle from '@hooks/usePageTitle/usePageTitle';
import styles from './SignUpVerifyUserOTPForm.module.scss';

interface ISignUpVerifyUserFormOTPProps {}

interface SignUpVerifyUserOTPForm {
  otp_code: string;
}

const SignUpVerifyUserOTPForm: FC<ISignUpVerifyUserFormOTPProps> = () => {
  usePageTitle('ZlaytAI - Verify Your Identity');

  const login = useAuthSelector((state) => state.login);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { signUpFormState } = useSignUpFormContext();

  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [countdown, setCountdown] = useState<number>(60);
  const [isCounting, setIsCounting] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [otpMethod, setOtpMethod] = useState<'email' | 'phone'>('email');
  const [phone, setPhone] = useState<string>('');
  const [isPhoneTouched, setIsPhoneTouched] = useState(false);

  const phoneRef = useMask({
    mask: '+1 (___) ___-____',
    // mask: '+55(__)_____-____',
    replacement: { _: /\d/ },
  });

  const validatePhoneNumber = (
    phoneNumber: string,
    countryCode: CountryCode = 'US'
  ): boolean => {
    if (!phoneNumber) return false;
    // let country_test:CountryCode = 'BR'
    const phoneValidation = parsePhoneNumberFromString(
      phoneNumber,
      countryCode
    );
    return phoneValidation?.isValid() || false;
  };

  const startCountdown = () => {
    setIsCounting(true);
    setCountdown(60);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsCounting(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!signUpFormState.email) {
      navigate('/sign-up');
    }
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SignUpVerifyUserOTPForm>({
    mode: 'onChange',
    defaultValues: {
      otp_code: SignUpFormDefaultValues.verifyCode,
    },
    resolver: yupResolver(
      object().shape({
        email: yup.string().notRequired(),
        otp_code: yup.string().required('Verification code is required'),
      })
    ),
  });

  const handleGenerateOTP = () => {
    const generateOTPRequest = {
      email: signUpFormState.email,
      phone: otpMethod === 'phone' ? phone : '',
      method: otpMethod,
    };
    generateOTP(
      generateOTPRequest,
      (data: GenerateOTPResponse) => {
        startCountdown();
        toast(({ id }) => (
          <Toast id={id} type="success" title="Success" text={data.message} />
        ));
      },
      (error) => {
        toast(({ id }) => (
          <Toast
            id={id}
            type="error"
            title="Error"
            text={error.response?.data?.error || 'Error generating OTP'}
          />
        ));
      }
    );
  };

  const handleNext = () => {
    reset({ otp_code: '' });
    if (otpMethod == 'email') {
      // Always generate OTP when user clicks "Get my Code" for email
      handleGenerateOTP();
      setStep(3);
    }
    if (otpMethod == 'phone') {
      if (step == 2) {
        handleGenerateOTP();
        setStep(3);
      } else if (step == 1) {
        hasPhoneNumber(
          signUpFormState.email,
          (data: { has_phone_number: boolean }) => {
            if (data.has_phone_number) {
              handleGenerateOTP();
              setStep(3);
            } else {
              setStep(2);
            }
          },
          (error: any) => {
            console.error('Error:', error);
          }
        );
      }
    }
  };

  const onSubmit = (data: SignUpVerifyUserOTPForm) => {
    const verifyUserRequestData = {
      email: signUpFormState.email,
      otp_code: data.otp_code,
      phone: otpMethod === 'phone' ? phone : undefined,
    };

    setIsSubmitting(true);
    verifyOTP(
      verifyUserRequestData,
      (data: VerifyOTPResponse) => {
        setIsSubmitting(false);
        toast(({ id }) => (
          <Toast
            id={id}
            type="success"
            title="Success"
            text="OTP successfully verified. Welcome to ZlaytAI."
          />
        ));
        login(data);
        navigate(routes.home);
      },
      (error: any) => {
        setIsSubmitting(false);
        // Prioritize message field (new format), fallback to error field for backward compatibility
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'An error occurred. Please try again.';
        toast(({ id }) => (
          <Toast id={id} type="error" title="Error" text={errorMessage} />
        ));
      }
    );
  };

  const handleResend = () => {
    setIsLoading(true);
    resendOTP(
      {
        email: signUpFormState.email,
        phone: otpMethod === 'phone' ? phone : null,
        method: otpMethod,
      },
      () => {
        setIsLoading(false);
        toast(({ id }) => (
          <Toast
            id={id}
            type="success"
            title={
              otpMethod === 'phone'
                ? "We've resent the code to your phone"
                : "We've resent the code to your email"
            }
            text={
              otpMethod === 'phone'
                ? 'Please check your phone.'
                : 'Please check your email.'
            }
          />
        ));
      },
      (error: any) => {
        setIsLoading(false);
        if (error.response.status == 429) {
          startCountdown();
          toast(({ id }) => (
            <Toast
              id={id}
              type="error"
              title="Error"
              text={error?.response?.data?.error}
            />
          ));
        } else {
          toast(({ id }) => (
            <Toast
              id={id}
              type="error"
              title="Error"
              text={error?.response?.data?.error}
            />
          ));
        }
      }
    );
  };

  const switchEmail = () => {
    setOtpMethod('email');
    setStep(1);
    reset({ otp_code: '' });
  };

  const switchPhone = () => {
    setOtpMethod('phone');
    setStep(2);
    reset({ otp_code: '' });
  };

  return (
    <div>
      {step === 1 ? (
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <Grid item xs={12}>
            <Grid
              container
              direction={{ xs: 'column-reverse', md: 'row' }}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item xs={12}>
                <Stack spacing={4} alignItems="center">
                  <Typography variant="h3">2-Step Login Required</Typography>
                  <Typography variant="h4">
                    How do you want to get your code?
                  </Typography>
                  <div className={styles.radioButtonOtp}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={otpMethod === 'email'}
                          onChange={() => setOtpMethod('email')}
                        />
                      }
                      label="Send code to my email"
                    />
                    <FormControlLabel
                      control={
                        <Radio
                          checked={otpMethod === 'phone'}
                          onChange={() => setOtpMethod('phone')}
                        />
                      }
                      label="Send code to my phone"
                    />
                  </div>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <form>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexGrow={1}
                  mt={2}
                >
                  <IconButton
                    type="button"
                    onClick={() => navigate(-1)}
                    className={styles.backBtn}
                  >
                    <ArrowIcon stroke="#667085" />
                  </IconButton>
                  <Button
                    type="button"
                    variant="active"
                    className={styles.nextBtn}
                    isLoading={isSubmitting}
                    onClick={handleNext}
                  >
                    Get my Code
                  </Button>
                </Box>
              </Grid>
            </form>
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
      {step === 2 && otpMethod === 'phone' ? (
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <Grid item xs={12}>
            <Grid
              container
              direction={{ xs: 'column-reverse', md: 'row' }}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item xs={12} alignItems="center">
                <Typography variant="h4">Phone Number</Typography>
                <FormInput
                  bordered
                  ref={phoneRef}
                  name="phone"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (!isPhoneTouched) setIsPhoneTouched(true);
                  }}
                  placeholder="+0 (000) 000-0000"
                  errorText={
                    isPhoneTouched && phone === ''
                      ? 'Phone number is a required field'
                      : isPhoneTouched && !validatePhoneNumber(phone)
                        ? 'Phone number is invalid'
                        : undefined
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <form>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexGrow={1}
                  mt={2}
                >
                  <IconButton
                    type="button"
                    onClick={() => setStep(1)}
                    className={styles.backBtn}
                  >
                    <ArrowIcon stroke="#667085" />
                  </IconButton>
                  <Button
                    type="button"
                    variant="active"
                    className={styles.nextBtn}
                    isLoading={isSubmitting}
                    onClick={handleNext}
                    disabled={
                      phone === '' ||
                      phone == null ||
                      validatePhoneNumber(phone) === false
                    }
                  >
                    Next
                  </Button>
                </Box>
              </Grid>
            </form>
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
      {step === 3 ? (
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          <Grid item xs={12}>
            <Grid
              container
              direction={{ xs: 'column-reverse', md: 'row' }}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item>
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  paddingX={3}
                >
                  <Typography
                    color="gray.main"
                    gutterBottom
                    variant={downMD ? 'h3' : 'h2'}
                    textAlign="center"
                  >
                    Type the Code We Sent You!
                  </Typography>
                  <Typography
                    color="gray"
                    gutterBottom
                    fontSize={14}
                    variant="subtitle2"
                    textAlign="center"
                  >
                    {`We sent a code to your ${otpMethod == 'email' ? 'email' : 'phone'}.`}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid item xs={12}>
                <FormInput
                  bordered
                  type="input"
                  labelText="Verification code"
                  placeholder="XXXXXX"
                  errorText={
                    errors?.otp_code && 'Verification code is a required field'
                  }
                  {...register('otp_code')}
                />
                {otpMethod === 'phone' ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography variant="body2" color="gray">
                      Want to use your email instead?
                    </Typography>
                    <Button
                      type="button"
                      variant="underline"
                      onClick={switchEmail}
                    >
                      Click here.
                    </Button>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography variant="body2" color="gray">
                      Want to use your phone instead?
                    </Typography>
                    <Button
                      type="button"
                      variant="underline"
                      onClick={switchPhone}
                    >
                      Click here.
                    </Button>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <Typography variant="body2" color="gray">
                    Didn't get the code?
                  </Typography>
                  <Button
                    variant="underline"
                    type="button"
                    onClick={handleResend}
                    disabled={isCounting || isLoading}
                    isLoading={isLoading}
                    className={`${styles.resendButton} ${isCounting ? styles.disabled : ''}`}
                  >
                    {isCounting
                      ? `Try again in ${formatTime(countdown)}`
                      : 'Resend'}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexGrow={1}
                  mt={2}
                >
                  <IconButton
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setPhone('');
                      setIsPhoneTouched(false);
                    }}
                    className={styles.backBtn}
                  >
                    <ArrowIcon stroke="#667085" />
                  </IconButton>
                  <Button
                    type="submit"
                    variant="active"
                    className={styles.nextBtn}
                    isLoading={isSubmitting}
                  >
                    Continue
                  </Button>
                </Box>
              </Grid>
            </form>
          </Grid>
        </Grid>
      ) : (
        <></>
      )}
    </div>
  );
};

export default SignUpVerifyUserOTPForm;
